<template>
    <div
        :class="{ disabled, skeleton, checked }"
        class="radio-component"
        @click="$emit('click', $event)"
    >
        <input
            v-if="!skeleton"
            :id="id || uuid"
            :checked="checked"
            :value="radioValue"
            :disabled="disabled"
            type="radio"
            @input="onClick"
        />
        <label
            v-if="!skeleton"
            :for="id || uuid"
            @click.stop
        >
            <Transition name="fadefast">
                <span v-if="checked" class="fill" />
            </Transition>
            <Transition name="fadefast">
                <span v-if="checked" class="fill-inner" />
            </Transition>
            <div v-if="$slots.default" class="slot-wrapper">
                <slot />
            </div>
        </label>
    </div>
</template>

<script>
// Generate UUID within <radio> scope for use in label/input id relationships.
let uid = 0;

export default {
    name: 'Radio',
    props: {
        value: { type: [String, Boolean], default: '' },
        radioValue: { type: [String, Boolean], default: '' },
        id: { type: String, default: undefined },

        skeleton: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false }
    },
    emits: ['click', 'update:value'],
    slots: ['default'],
    data() {
        return {
            uuid: undefined
        };
    },
    computed: {
        checked() {
            return this.value === this.radioValue;
        }
    },
    created() {
        this.uuid = `vue-component-radio-${uid}`;
        uid += 1;
    },
    methods: {
        onClick() {
            if (this.disabled) return;
            this.$emit('update:value', this.radioValue);
        }
    }
};
</script>

<style lang="less" scoped>

.radio-component {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    position: relative;
    color: var(--coal);
    min-height: 24px;
    min-width: 24px;
    background: 0;

    &:hover:not(.disabled) {
        label::before { border-color: var(--primary); }
    }

    &.disabled {
        cursor: default;
        color: var(--steel);
        input {
            &:indeterminate + label, &:checked + label {
                &:before {
                    background-color: transparent;
                    border-color: var(--steel);
                }
                .fill {
                    background-color: var(--steel);
                }
            }
        }
        label { cursor: default; }
    }
    &.indeterminate { opacity: 0.5; }

    &.success {
        input {
            &:checked + label {
                &::before { border-color: var(--success); }
                > .fill { background-color: var(--success); }
            }
        }

        &:hover:not(.disabled) {
            label::before { border-color: var(--success); }
        }
    }

    input {
        visibility: hidden;
        position: absolute;
        margin: 0;

        &:checked + label {
            &::before { border-color: var(--primary); }
            > .fill { background-color: var(--primary); }
        }
    }

    label {
        cursor: pointer;
        position: relative;
        min-width: 24px;
        min-height: 24px;
        margin: 0;
        padding: 0;

        &:before {
            content: '';
            transition: var(--smooth-fast);
            position: absolute;
            margin: auto;
            left: 0;
            width: 16px;
            height: 16px;
            margin: 2px;
            border-radius: 100%;
            background-color: #FFF;
            border: 1.5px solid var(--anchor);
        }
        .fill {
            position: absolute;
            margin: auto;
            left: 0;
            margin: 2px;
            width: 16px;
            height: 16px;
            border-radius: 100%;
        }
        .fill-inner {
            position: absolute;
            margin: auto;
            left: 0;
            margin: 7px;
            width: 6px;
            height: 6px;
            border-radius: 100%;
            background-color: #FFF;
        }
        .slot-wrapper {
            padding: 0 8px 0 24px;
            font-weight: normal;
            > * { margin: 0; }
        }
    }
}
</style>

<style lang="less">
.radio-component label p { margin: 0; }
</style>
